/**
 * Content API – Diya Charitable Trust
 * Reads CMS content (photos, YouTube videos, bilingual text, running notes) from
 * the private admin backend. When VITE_CONTENT_API_URL is set, the public site
 * uses this for dynamic content; otherwise every call returns an empty/null value
 * so the hardcoded defaults in activities.ts / i18n remain in effect.
 */

const PRODUCTION_CONTENT_API = 'https://dctadmin.com';

const getContentApiBase = (): string => {
  const url = typeof import.meta !== 'undefined' && import.meta.env?.VITE_CONTENT_API_URL;
  if (url && typeof url === 'string' && url.trim()) {
    return url.replace(/\/$/, '');
  }
  // CMS lives on dctadmin.com; don't require a build secret on GoDaddy deploys.
  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    if (host.includes('dctnow.ngo') || host.includes('diyacharity.org')) {
      return PRODUCTION_CONTENT_API;
    }
    // Local preview (npm run dev) — use live CMS so home photos render without extra setup
    if (host === 'localhost' || host === '127.0.0.1') {
      return PRODUCTION_CONTENT_API;
    }
  }
  return '';
};

/** Photo collections / activity ids managed by the CMS. Keep in sync with the API whitelist. */
export type PhotoCollection =
  | 'home'
  | 'budding-minds'
  | 'youth-leap'
  | 'vulnerable-women-families'
  | 'old-age-care'
  | 'environment'
  | 'annathanam';

export type ActivityId = Exclude<PhotoCollection, 'home'>;

export interface RunningNotes {
  en: string;
  ta: string;
}

export interface Bilingual {
  en: string;
  ta: string;
}

export interface ActivityContent {
  title: Bilingual;
  paragraphs: Bilingual[];
  caption: Bilingual;
}

interface ApiEnvelope<T> {
  success: boolean;
  data?: T;
  error?: string;
}

const fetchJson = async <T>(url: string, options?: RequestInit): Promise<T> => {
  const res = await fetch(url, { ...options, headers: { ...(options?.headers as Record<string, string>) } });
  const data = await res.json();
  if (!res.ok) throw new Error((data as { error?: string }).error || res.statusText);
  return data as T;
};

/**
 * Uploaded files are hosted by the content API (a different origin/port than the
 * public site), so prefix `/uploads/...` with the API base. Bundled `/assets/...`
 * paths are served by the site itself and returned unchanged.
 */
export function resolveContentUrl(url: string): string {
  const base = getContentApiBase();
  if (base && url.startsWith('/uploads/')) {
    return `${base}${url}`;
  }
  return url;
}

/** GET photo URLs for a collection. Returns [] when the API is unset or fails. */
export async function getPhotos(collection: PhotoCollection): Promise<string[]> {
  const base = getContentApiBase();
  if (!base) return [];
  try {
    const data = await fetchJson<ApiEnvelope<{ photos: string[] }>>(
      `${base}/api/photos?collection=${encodeURIComponent(collection)}&t=${Date.now()}`,
      { cache: 'no-store' }
    );
    const photos = data.success && data.data?.photos ? data.data.photos : [];
    return photos.map(resolveContentUrl);
  } catch {
    return [];
  }
}

/** GET YouTube video IDs for an activity. Returns [] when the API is unset or fails. */
export async function getVideos(activity: ActivityId): Promise<string[]> {
  const base = getContentApiBase();
  if (!base) return [];
  try {
    const data = await fetchJson<ApiEnvelope<{ videos: { videoId: string }[] }>>(
      `${base}/api/videos?activity=${encodeURIComponent(activity)}&t=${Date.now()}`,
      { cache: 'no-store' }
    );
    if (!data.success || !data.data?.videos) return [];
    return data.data.videos.map((v) => v.videoId).filter(Boolean);
  } catch {
    return [];
  }
}

/** GET bilingual text content for an activity. Returns null when the API is unset or fails. */
export async function getActivityContent(activity: ActivityId): Promise<ActivityContent | null> {
  const base = getContentApiBase();
  if (!base) return null;
  try {
    const data = await fetchJson<ApiEnvelope<ActivityContent>>(
      `${base}/api/content?activity=${encodeURIComponent(activity)}&t=${Date.now()}`,
      { cache: 'no-store' }
    );
    return data.success && data.data ? data.data : null;
  } catch {
    return null;
  }
}

/** GET home event photo URLs (back-compat alias of getPhotos('home')). */
export async function getHomePhotos(): Promise<string[]> {
  return getPhotos('home');
}

/** GET annathanam event photo URLs (back-compat alias of getPhotos('annathanam')). */
export async function getAnnathanamPhotos(): Promise<string[]> {
  return getPhotos('annathanam');
}

/** GET running notes (en, ta). Returns null when the API is unset or fails. */
export async function getRunningNotes(): Promise<RunningNotes | null> {
  const base = getContentApiBase();
  if (!base) return null;
  try {
    const data = await fetchJson<ApiEnvelope<RunningNotes>>(
      `${base}/api/running-notes?t=${Date.now()}`,
      { cache: 'no-store' }
    );
    return data.success && data.data ? data.data : null;
  } catch {
    return null;
  }
}

/**
 * Pick the language-appropriate string from a bilingual pair, falling back to the
 * other language, then to the provided default.
 */
export function pickBilingual(pair: Bilingual | undefined, language: 'en' | 'ta', fallback = ''): string {
  if (!pair) return fallback;
  const primary = language === 'ta' ? pair.ta : pair.en;
  const secondary = language === 'ta' ? pair.en : pair.ta;
  return (primary && primary.trim()) || (secondary && secondary.trim()) || fallback;
}

export function getContentApiBaseUrl(): string {
  return getContentApiBase();
}

/** @deprecated Use getContentApiBaseUrl() — module init ran at build time without window. */
export const contentApiBase = typeof window !== 'undefined' ? getContentApiBase() : '';
